import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';


const styles = theme => ({
  root: {
    flexGrow: 1
  },
  list: {
    maxHeight: 400,
    height: 400,
    marginTop: 10,
    overflow: 'auto'
  },
  subheader: {
    backgroundColor: theme.palette.common.white,
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.background.default
  },
  primaryText: {
    fontWeight: 'bold'
  },
  secondaryText: {
    fontSize: 12
  }
});

class TypeToSearch extends Component {
  state = {
    value: '',
    selectedIndex: -1
  }

  constructor(props) {
    super(props);
    this.listRef = React.createRef();
    this.selRef = React.createRef();
  }

  componentDidUpdate() {
    this.ensureSelectionIsVisible();
  }

  ensureSelectionIsVisible = () => {
    const list = this.listRef.current;
    const item = this.selRef.current;
    if (!list || !item) {
      return;
    }
    const itemTop = item.offsetTop;
    if (itemTop < list.scrollTop) {
      list.scrollTop = itemTop;
      return;
    }

    const itemBottom = item.offsetTop + item.offsetHeight;
    const scrollBottom = list.scrollTop + list.offsetHeight;
    if (itemBottom > scrollBottom) {
      list.scrollTop = itemBottom - list.offsetHeight;
    }


  }

  handleChange = evt => {
    const { value } = evt.target;
    this.setState({ value });

    if (value.length > 2) {
      this.props.onChange(evt);
    }
  }

  handleClick = suggestion => (/* evt */) => {
    this.setState({
      value: '',
      selectedIndex: -1
    }, () => this.props.onSelect(suggestion));
  }

  handleSecondaryActopmClick = (action, suggestion) => () => {
    action.onClick(suggestion);
  }

  createItem = (suggestion, index) => {
    const { classes, primary, secondary, onGetActions, onSecondaryAction } = this.props;
    const { selectedIndex } = this.state;
    const isSelected = selectedIndex === index;
    let ref = React.createRef();
    if (isSelected) {
      this.selRef = React.createRef();
      ref = this.selRef;
    }    
    let secondaryActions = <></>
    let disablePrimaryAction = false;
    if (onGetActions) {
      const bindAction = (tag, value) => () => {
        this.setState({
          value: '',
          selectedIndex: -1
        }, () => onSecondaryAction(tag, value));
      }
      const actions = onGetActions(suggestion);
      disablePrimaryAction = actions.disablePrimary;
      secondaryActions = (<>
        {(actions.secondary || []).map(a => (
          <IconButton edge="end" aria-label={a.label} onClick={bindAction(a.tag, suggestion)}>
              {a.icon}
          </IconButton>
        ))}
      </>)
    }
    return (
      <ListItem ref={ref}
        button
        onClick={disablePrimaryAction ? undefined : this.handleClick(suggestion)}
        selected={isSelected}
      >
        <ListItemText
          primary={suggestion[primary]}
          secondary={secondary(suggestion)}
          classes={{
            primary: classes.primaryText,
            secondary: classes.secondaryText
          }}
        />
        <ListItemSecondaryAction>
          {secondaryActions}
        </ListItemSecondaryAction>
      </ListItem>
    )
  }

  handleKeyDown = evt => {
    switch (evt.which) {
      case 27:
        this.closeSuggestions();
        break;
      case 13:
        evt.preventDefault();
        this.selectCurrentItem();
        break;
      case 38:
        evt.preventDefault();
        this.moveSelectedIndex(-1);
        break;
      case 40:
        evt.preventDefault();
        this.moveSelectedIndex(1);
        break;
      default:
    }

  }

  closeSuggestions = () => {
    this.setState({
      value: '',
      selectedIndex: -1
    });
  }

  selectCurrentItem = () => {
    const { selectedIndex } = this.state;
    const { suggestions } = this.props;
    if (selectedIndex < 0) return;
    if (selectedIndex >= suggestions.length) return;
    const suggestion = suggestions[selectedIndex];
    this.handleClick(suggestion)();
  }

  moveSelectedIndex = moveBy => {
    const { suggestions } = this.props;
    let { selectedIndex } = this.state;
    if (selectedIndex === -1) {
      selectedIndex = 0
    } else {
      selectedIndex += moveBy;
      selectedIndex = Math.max(selectedIndex, 0);
      selectedIndex = Math.min(selectedIndex, suggestions.length - 1);
    }
    this.setState({ selectedIndex });
  }

  showSelectedIndex = show => () => {
    if (!show) {
      this.setState({ selectedIndex: -1 });
    }
  }

  render() {
    const {
      name,
      label,
      classes,
      disabled,
      placeholder,
      suggestions
    } = this.props;

    const { value } = this.state;

    return (
      <div className={classes.root}>
        <TextField
          variant="standard"
          fullWidth
          label={label}
          name={name}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={this.handleChange}
          onKeyDown={this.handleKeyDown}
          onBlur={this.showSelectedIndex(false)} />
        {value.length > 2 && (
        <Paper elevation={1} square>
          <List ref={this.listRef}
            className={classes.list}
            open={suggestions.length > 0 && value.length > 0}
          >
            {suggestions.map(this.createItem)}
          </List>
        </Paper>
        )}
      </div>
    );
  }
}

TypeToSearch.defaultProps = {
  classes: null,
  suggestions: null,
  primary: null,
  secondary: null,
  placeholder: null,
  disabled: false
}

TypeToSearch.propTypes = {
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  classes: PropTypes.object,
  suggestions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  primary: PropTypes.string,
  secondary: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool
}

export default  withStyles(styles)(TypeToSearch);
